<template>
  <PageHeader
    breadcrumb
    :items="[{ label: 'Reports', to: '/reports' }, { label: 'Call Centers' }, { label: 'Live Agent Calls' }]"
  >
    <template #right>
      <div class="flex items-center gap-4">
        <Select
          v-model="callCenterSelected"
          :options="callCentersAllowed"
          icon="headset"
          value-key="id"
          label-key="name"
          placeholder="Select a Call Center"
          class="min-w-56"
          :readonly="callCentersAllowed.length === 1"
          @select="updateCallCenterSelected"
        />
      </div>
    </template>
  </PageHeader>

  <div>
    <Card :is-loading="fetchUsersRequest.isLoading.value">
      <div
        class="schedule-header grid"
        :style="'grid-template-columns: repeat(9, minmax(0, 1fr));'"
      >
        <div class="schedule-header-cell col-span-2">
          <Input
            v-model="search"
            icon="search"
            placeholder="Search"
          />
        </div>
      </div>

      <div v-if="usersComputed.length">
        <div
          v-for="user in usersComputed"
          :key="user.id"
          class="schedule-row grid"
          :style="'grid-template-columns: repeat(12, minmax(0, 1fr));'"
        >
          <div class="row-side col-span-3 flex justify-between">
            <AvatarLabelGroup
              :label="user.fullName"
              :description="user.agentStatusDisplay"
              :avatar-url="user.profilePicUrl"
              :avatar-text="user.initials"
              :status-indicator-icon="user.presenceIcon"
              :status-indicator-color-class="user.presenceColorClass"
            >
              <template #description>
                <div class="text-sm text-secondary">
                  {{ capitalize(user.presence) }}
                </div>
              </template>
            </AvatarLabelGroup>
            <Chip
              v-if="user.agentChip"
              :label="user.agentChip.label"
              :icon="user.agentChip.icon"
              size="sm"
              :severity="user.agentChip.severity"
            />
          </div>

          <div
            v-for="call in user.conferences"
            :key="call.id"
            class="col-span-2"
          >
            <div class="call-block">
              <div
                class="call-block__icon flex items-center justify-center h-full"
                :class="call.iconClass"
              >
                <Icon
                  fa-style="fas"
                  :type="call.iconType"
                />
              </div>
              <div class="px-3 py-2 w-full">
                <div class="font-medium">
                  {{ call.direction === 'outbound' ? call.to : call.from }}
                </div>
                <div class="text-sm text-secondary">{{ call.stage }}</div>
              </div>
              <div
                v-if="call.status === 'in-progress'"
                class="audio-wave pr-4"
              >
                <svg
                  id="wave"
                  data-name="Layer 1"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 50 38.05"
                >
                  <title>Audio Wave</title>
                  <path
                    id="Line_1"
                    data-name="Line 1"
                    d="M0.91,15L0.78,15A1,1,0,0,0,0,16v6a1,1,0,1,0,2,0s0,0,0,0V16a1,1,0,0,0-1-1H0.91Z"
                  />
                  <path
                    id="Line_2"
                    data-name="Line 2"
                    d="M6.91,9L6.78,9A1,1,0,0,0,6,10V28a1,1,0,1,0,2,0s0,0,0,0V10A1,1,0,0,0,7,9H6.91Z"
                  />
                  <path
                    id="Line_3"
                    data-name="Line 3"
                    d="M12.91,0L12.78,0A1,1,0,0,0,12,1V37a1,1,0,1,0,2,0s0,0,0,0V1a1,1,0,0,0-1-1H12.91Z"
                  />
                  <path
                    id="Line_4"
                    data-name="Line 4"
                    d="M18.91,10l-0.12,0A1,1,0,0,0,18,11V27a1,1,0,1,0,2,0s0,0,0,0V11a1,1,0,0,0-1-1H18.91Z"
                  />
                  <path
                    id="Line_5"
                    data-name="Line 5"
                    d="M24.91,15l-0.12,0A1,1,0,0,0,24,16v6a1,1,0,0,0,2,0s0,0,0,0V16a1,1,0,0,0-1-1H24.91Z"
                  />
                  <path
                    id="Line_6"
                    data-name="Line 6"
                    d="M30.91,10l-0.12,0A1,1,0,0,0,30,11V27a1,1,0,1,0,2,0s0,0,0,0V11a1,1,0,0,0-1-1H30.91Z"
                  />
                  <path
                    id="Line_7"
                    data-name="Line 7"
                    d="M36.91,0L36.78,0A1,1,0,0,0,36,1V37a1,1,0,1,0,2,0s0,0,0,0V1a1,1,0,0,0-1-1H36.91Z"
                  />
                  <path
                    id="Line_8"
                    data-name="Line 8"
                    d="M42.91,9L42.78,9A1,1,0,0,0,42,10V28a1,1,0,1,0,2,0s0,0,0,0V10a1,1,0,0,0-1-1H42.91Z"
                  />
                  <path
                    id="Line_9"
                    data-name="Line 9"
                    d="M48.91,15l-0.12,0A1,1,0,0,0,48,16v6a1,1,0,1,0,2,0s0,0,0,0V16a1,1,0,0,0-1-1H48.91Z"
                  />
                </svg>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        v-else
        class="py-32"
      >
        <Empty
          title="No users found"
          icon="users"
        >
          <template #buttons>
            <Button
              v-if="callCenterSelected"
              label="Add users"
              :router-link="`/settings/call-centers/${callCenterSelected}/users`"
            />
          </template>
        </Empty>
      </div>
    </Card>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { capitalize } from 'lodash'
import PageHeader from '@/components/page/PageHeader.vue'
import Card from '@/components/card/Card.vue'
import Icon from '@/components/icon/Icon.vue'
import Empty from '@/components/empty/Empty.vue'
import Select from '@/components/forms/Select.vue'
import Chip from '@/components/chip/Chip.vue'
import AvatarLabelGroup from '@/components/avatar/AvatarLabelGroup.vue'
import Button from '@/components/button/Button.vue'
import Input from '@/components/forms/Input.vue'
import { alertToast } from '@/utilities/notification'
import useApiRequest from '@/composables/useApiRequest'
// stores
import { useCallCenterStore } from '@/stores/CallCenterStore.ts'
import { useSocketStore } from '@/stores/SocketStore.ts'

const callCenterStore = useCallCenterStore()
const socketStore = useSocketStore()
const users = ref(null)
const search = ref('')
const fetchUsersRequest = useApiRequest()
const callCentersAllowed = callCenterStore.callCenters
const callCenterSelected = ref(parseInt(localStorage.getItem('report-filter-call-center-selected')) || null)

function updateCallCenterSelected() {
  localStorage.setItem('report-filter-call-center-selected', callCenterSelected.value)
  fetchUsers()
}

function fetchUsers() {
  if (callCenterSelected.value === null) return

  try {
    fetchUsersRequest
      .send({
        method: 'GET',
        endpoint: '/v1/users',
        params: {
          userType: 'all',
          callCenterId: callCenterSelected.value
        }
      })
      .then(response => {
        if (response.success) {
          users.value = response.data.length ? response.data.sort((a, b) => a.fullName.localeCompare(b.fullName)) : []
        } else {
          alertToast('Failed to fetch users', response.message, 'error')
        }
      })
  } catch (err) {
    alertToast('Failed to fetch users', err.message, 'error')
  }
}
fetchUsers()

// Pre-format conferences with extra computed fields.
const conferences = computed(() => {
  return socketStore.callCenterConferences.map(conference => {
    let stage = conference.status
    if (conference.status === 'waiting') {
      stage = 'Waiting'
    } else if (conference.status === 'in-progress') {
      stage = 'In-Progress'
    } else if (conference.status === 'completed' && !conference.isWrappedUp) {
      stage = 'Wrapping Up'
    }
    const iconType =
      conference.status === 'waiting' ? 'hourglass' : conference.status === 'in-progress' ? 'phone' : 'list-check'
    const iconClass = `call-block__icon--${conference.status}`
    return { ...conference, stage, iconType, iconClass }
  })
})

// Enrich each user with computed status values and conferences.
const usersComputed = computed(() => {
  const chipMapping = {
    available: { icon: 'check', severity: 'success' },
    'on-call': { icon: 'phone', severity: 'danger' },
    break: { icon: 'pause', severity: 'warning' },
    offline: { icon: 'x', severity: 'default' }
  }

  return (users.value || [])
    .map(user => {
      const active = socketStore.activeUsers.find(u => u.id === user.id) || {}
      user.presence = active.status || 'offline'
      user.agentStatus = active.agentStatus || ''
      user.presenceIcon = !user.presence || user.presence === 'offline' ? 'x' : 'check'
      user.presenceColorClass =
        user.presence === 'online'
          ? 'bg-success-solid'
          : user.presence === 'idle'
            ? 'bg-warning-solid'
            : 'bg-secondary-solid'
      user.agentStatusDisplay = user.agentStatus ? capitalize(user.agentStatus) : ''
      user.agentChip = chipMapping[user.agentStatus]
        ? { label: user.agentStatusDisplay, ...chipMapping[user.agentStatus] }
        : null
      user.conferences = conferences.value.filter(conference => conference.agentId === user.id)
      return user
    })
    .filter(u => !search.value || u.fullName.toLowerCase().includes(search.value.toLowerCase()))
})
</script>

<style scoped>
.schedule-header {
  border-bottom: 1px solid var(--border-secondary);
  position: sticky;
  top: 0;
  z-index: 1;
  border-radius: var(--rounded-md) var(--rounded-md) 0 0;
}

.schedule-header-cell {
  padding: 0.6rem 1rem;
}

.schedule-row {
  border-bottom: 1px solid var(--border-secondary);
}

.row-side {
  padding: 0.5rem 1rem;
  display: flex;
  align-items: center;
  border-right: 1px solid var(--border-secondary);
  min-height: 62px;
}

.call-block {
  background-color: var(--bg-tertiary);
  border-radius: var(--rounded-md);
  height: 54px;
  margin: var(--s-1);
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
}

.call-block__icon {
  min-width: 30px;
}

.call-block__icon--waiting {
  color: var(--text-warning);
  background-color: var(--bg-warning-secondary);
}

.call-block__icon--in-progress {
  color: var(--text-success);
  background-color: var(--bg-success-secondary);
}

.call-block__icon--completed {
  color: var(--text-info);
  background-color: var(--bg-info-secondary);
}

.audio-wave {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 20px;
}

#wave {
  height: 32px;
  width: 32px;
  fill: var(--fg-info);
}

@keyframes pulse {
  0% {
    transform: scaleY(1);
    transform-origin: 50% 50%;
  }

  50% {
    transform: scaleY(0.7);
    transform-origin: 50% 50%;
  }

  100% {
    transform: scaleY(1);
    transform-origin: 50% 50%;
  }
}

#Line_1 {
  animation: pulse 1s infinite;
  animation-delay: 0.15s;
}

#Line_2 {
  animation: pulse 1s infinite;
  animation-delay: 0.3s;
}

#Line_3 {
  animation: pulse 1s infinite;
  animation-delay: 0.45s;
}

#Line_4 {
  animation: pulse 1s infinite;
  animation-delay: 0.6s;
}

#Line_5 {
  animation: pulse 1s infinite;
  animation-delay: 0.75s;
}

#Line_6 {
  animation: pulse 1s infinite;
  animation-delay: 0.9s;
}

#Line_7 {
  animation: pulse 1s infinite;
  animation-delay: 1.05s;
}

#Line_8 {
  animation: pulse 1s infinite;
  animation-delay: 1.2s;
}

#Line_9 {
  animation: pulse 1s infinite;
  animation-delay: 1.35s;
}
</style>
